import { CurrencyPipe } from "@angular/common";
import {
  Component,
  effect,
  inject,
  input,
  OnInit,
  ViewChild,
  ViewEncapsulation,
} from "@angular/core";
import { TranslateModule } from "@ngx-translate/core";
import { Observable, of } from "rxjs";
import { MaterialModule } from "src/app/material.module";
import { StoreFacadeService } from "src/app/pages/store-management/stores/stores-facade.service";
import {
  TableColumnTemplateDirective,
  TableResponsiveCardTemplateDirective,
  TreesloGridComponent,
  TreesloGridLoadRequest,
  TreesloGridLoadResult,
  TreesloGridModel,
} from "src/app/shared/components/treeslo-grid";
import { Widgets } from "src/app/shared/components/treeslo-grid/widgets";
import {
  applyGridColumnOrderChange,
  withReorderableGridColumns,
} from "src/app/shared/helpers/grid-column-order-view-state.helper";
import {
  DASHBOARD_VIEW_STATE_KEY,
  DashboardViewCachedState,
} from "src/app/shared/helpers/grid-view-state-keys.helper";
import { ProviderGetDTO } from "src/app/pages/providers/provider.model";
import { ViewStateCacheService } from "src/app/shared/services/view-state-cache.service";
import { StoreProviderReportWidgetKpi } from "../../models/dashboard-widget-kpi.model";
import { ProviderReportData } from "../../models/provider-report.model";
import { StoreProviderReportResponsiveCardComponent } from "../../store-provider-report-responsive-card/store-provider-report-responsive-card.component";

@Component({
  selector: "app-dashboard-store-provider-report-widget",
  standalone: true,
  imports: [
    MaterialModule,
    TranslateModule,
    TreesloGridComponent,
    TableColumnTemplateDirective,
    TableResponsiveCardTemplateDirective,
    StoreProviderReportResponsiveCardComponent,
  ],
  templateUrl: "./store-provider-report-widget.component.html",
  encapsulation: ViewEncapsulation.None,
})
export class StoreProviderReportWidgetComponent implements OnInit {
  private readonly facadeSvc = inject(StoreFacadeService);
  private readonly viewStateCache = inject(ViewStateCacheService);

  readonly kpi = input.required<StoreProviderReportWidgetKpi>();
  readonly providers = input.required<ProviderGetDTO[]>();

  @ViewChild("storeProviderReportGrid")
  storeProviderReportGrid?: TreesloGridComponent<ProviderReportData>;

  gridConfig!: TreesloGridModel<ProviderReportData>;
  private storeProviderReportColumnOrder?: string[];

  constructor() {
    effect(() => {
      this.kpi().rows;
      queueMicrotask(() => this.refreshGrid());
    });
  }

  ngOnInit(): void {
    const cachedViewState =
      this.viewStateCache.getState<DashboardViewCachedState>(
        DASHBOARD_VIEW_STATE_KEY,
      );
    this.storeProviderReportColumnOrder =
      cachedViewState?.storeProviderReportColumnOrder;
    this.gridConfig = this.buildGridConfig();
  }

  onStoreProviderReportGridColumnOrderChange(columnOrder: string[]): void {
    this.storeProviderReportColumnOrder = columnOrder;
    this.gridConfig = applyGridColumnOrderChange(
      columnOrder,
      this.gridConfig,
    ) as TreesloGridModel<ProviderReportData>;
    this.persistDashboardViewCache();
  }

  refreshGrid(): void {
    this.storeProviderReportGrid?.refresh();
  }

  getProviderName(providerKey?: string): string {
    if (!providerKey) {
      return "";
    }

    const match = this.findProviderMatch(providerKey);
    return match?.name || providerKey;
  }

  getProviderLogo(providerKey?: string): string {
    const match = this.findProviderMatch(providerKey ?? "");
    return this.facadeSvc.getProviderLogo(match?.value || providerKey);
  }

  isAmazonProvider(providerKey?: string): boolean {
    const normalized = (providerKey || "").toLowerCase();
    const match = this.findProviderMatch(providerKey ?? "");
    const providerValue = (match?.value || providerKey || "").toLowerCase();
    return providerValue.includes("amazon") || normalized.includes("amazon");
  }

  private buildGridConfig(): TreesloGridModel<ProviderReportData> {
    return withReorderableGridColumns(
      {
        striped: true,
        allowResponsive: true,
        translationPrefix: "dashboard.column_store_provider_report",
        selectFirstRowOnLoad: true,
        activeRowIndex: 0,
        load: (request) => this.loadGrid(request),
        columns: [
          { displayfieldName: "provider", fieldName: "provider" },
          {
            displayfieldName: "filed_cases",
            fieldName: "filedCases",
            align: "right",
          },
          {
            displayfieldName: "claim_success_rate",
            fieldName: "claimSuccessRate",
            align: "center",
            widget: () =>
              Widgets.ProgressBar({
                width: "6.5rem",
                evaluator: (percentage) => {
                  if (percentage < 20) {
                    return "danger";
                  }
                  if (percentage < 50) {
                    return "warning";
                  }
                  if (percentage < 80) {
                    return "info";
                  }
                  return "success";
                },
              }),
          },
          {
            displayfieldName: "cash_reembursement",
            fieldName: "cashReimbursement",
            pipes: [{ pipe: CurrencyPipe }],
            align: "right",
          },
          {
            displayfieldName: "inventory_reembursement",
            fieldName: "inventoryReimbursement",
            pipes: [{ pipe: CurrencyPipe }],
            align: "right",
          },
          {
            displayfieldName: "total_reimbursement",
            fieldName: "totalReimbursement",
            pipes: [{ pipe: CurrencyPipe }],
            align: "right",
          },
          {
            displayfieldName: "unclaimed_total",
            fieldName: "unclaimedTotal",
            align: "right",
          },
          {
            displayfieldName: "unclaimed_amount",
            fieldName: "unclaimedAmount",
            pipes: [{ pipe: CurrencyPipe }],
            align: "right",
          },
        ],
      },
      this.storeProviderReportColumnOrder,
    );
  }

  private loadGrid(
    _request: TreesloGridLoadRequest,
  ): Observable<TreesloGridLoadResult<ProviderReportData>> {
    return of({
      items: this.kpi().rows,
      cursor: null,
    });
  }

  private persistDashboardViewCache(): void {
    this.viewStateCache.setState(DASHBOARD_VIEW_STATE_KEY, {
      storeProviderReportColumnOrder: this.storeProviderReportColumnOrder,
    });
  }

  private findProviderMatch(providerKey: string): ProviderGetDTO | undefined {
    const normalizedKey = providerKey.trim().toLowerCase();
    return this.providers().find(
      (provider) =>
        provider.value === providerKey ||
        provider.name === providerKey ||
        provider.value.toLowerCase() === normalizedKey ||
        provider.name.toLowerCase() === normalizedKey,
    );
  }
}
